export interface StoreLink {
  type: "appstore" | "playstore" | "youtube";
  href: string;
}

export interface MobileApp {
  id: string;
  name: string;
  description: string[];
  stack?: { label: string; value: string }[];
  links: StoreLink[];
  screenshots: { src: string; width: number; height: number }[];
}

const shots = (prefix: string, count: number, width: number, height: number) =>
  Array.from({ length: count }, (_, i) => ({
    src: `/images/${prefix}-${i + 1}.webp`,
    width,
    height,
  }));

export const mobileApps: MobileApp[] = [
  {
    id: "grup-bul",
    name: "Grup Bul",
    description: [
      "Discover your perfect musical match with our cross-platform app, designed for musicians seeking bandmates and bands looking for that missing piece. Whether you’re a band short of one key member or a talented musician looking to join a group, our app bridges the gap.",
      "Easily post and search for opportunities by specifying genre, city, and position. Tailor your search with filters for instruments and genres to find your ideal band or bandmate. Connect instantly using our real-time chat feature and stay updated with handy notifications. It’s time to turn your musical dreams into reality – join our community and find your musical soulmate today!",
    ],
    stack: [
      { label: "Frontend", value: "Flutter, Provider, Firebase Cloud Messaging" },
      {
        label: "Backend",
        value: "FastAPI, Docker, MongoDB, GraphQL, WebSockets (Custom Real-Time Chat), Redis, Firebase Storage",
      },
      { label: "Cloud & CI/CD", value: "AWS, GitHub Actions, GitHub" },
    ],
    links: [
      { type: "appstore", href: "https://apps.apple.com/tr/app/grupbul/id6475634499" },
      { type: "playstore", href: "https://play.google.com/store/apps/details?id=com.dorukarslan.grooveGather" },
      { type: "youtube", href: "https://www.youtube.com/watch?v=lJ7rBdcPv78" },
    ],
    screenshots: shots("grupBul", 5, 436, 938),
  },
  {
    id: "spycall",
    name: "SpyCall",
    description: [
      "SpyCall transforms the classic SpyFall game by introducing a wide range of categories and captivating locations. Its standout feature allows users to tailor their own game categories and places.",
      "With multi-language support in English, German, and Turkish, the application is developed using the Flutter framework. Every design and visuals were produced by me using various tools.",
    ],
    stack: [
      { label: "Frontend", value: "Flutter, Provider, Firebase Cloud Messaging" },
      {
        label: "Backend",
        value: "FastAPI, Docker, SQL, WebSockets (Custom Real-Time Chat), Redis, Firebase Storage",
      },
      { label: "Cloud & CI/CD", value: "AWS, GitHub Actions, GitHub" },
    ],
    links: [
      { type: "appstore", href: "https://apps.apple.com/tr/app/spycall-offline/id6465287862" },
      { type: "playstore", href: "https://play.google.com/store/apps/details?id=com.dorukarslan.spycall" },
      { type: "youtube", href: "https://www.youtube.com/watch?v=5JR0sJuQD04" },
    ],
    screenshots: shots("spyCall", 4, 546, 1186),
  },
  {
    id: "clouddiagnostix-tozero",
    name: "CloudDiagnostix ToZero",
    description: [
      "ToZeroCloudLog: This application was developed to enable real-time diagnostics and monitoring for electric buses, providing insights into critical metrics such as State of Charge (SOC), cell voltages, temperatures, and power output.",
      "Data is collected using Teltonika devices installed on the buses, which transmit detailed diagnostic messages to a cloud-based backend hosted on AWS. These raw messages are decoded based on Teltonika’s documentation to extract the relevant values. The decoded data is then further processed using DBC files to convert hexadecimal values into readable decimal metrics.",
      "This processed information is delivered to a Flutter-based mobile application via a WebSocket connection, providing operators with a user-friendly interface for tracking vehicle performance in real time. The solution integrates IoT hardware, cloud infrastructure, and advanced data processing to ensure seamless and reliable diagnostics for electric vehicle fleets.",
    ],
    stack: [
      { label: "Mobile", value: "Flutter, Riverpod, FCM" },
      {
        label: "Backend",
        value: "FastAPI, WebSockets, Redis",
      },
      {
        label: "Cloud & DevOps",
        value: "Docker, GitHub Actions, EKS, ECR, RDS, S3, Cloudflare",
      },
    ],
    links: [
      { type: "appstore", href: "https://apps.apple.com/tr/app/clouddiagnostix-tozero/id6648793268" },
      { type: "playstore", href: "https://play.google.com/store/apps/details?id=com.dorukarslan.diagnosis" },
    ],
    screenshots: shots("CloudDiagnostixToZero", 4, 703, 1440),
  },
  {
    id: "learnflow",
    name: "LearnFlow",
    description: [
      "Stay focused. Create as many workspaces as you want with LearnFlow! You can start work on the workspaces you have created, add the work you have done, and view the work analysis. Stay focused with LearnFlow.",
    ],
    stack: [
      { label: "Frontend", value: "Flutter, Provider, Firebase Cloud Messaging" },
      {
        label: "Backend",
        value: "FastAPI, Docker, MongoDB, WebSockets (Custom Real-Time Chat), Redis, Firebase Storage",
      },
      { label: "Cloud & CI/CD", value: "AWS, GitHub Actions, GitHub" },
    ],
    links: [
      { type: "appstore", href: "https://apps.apple.com/tr/app/learnflow/id6446949338" },
      { type: "playstore", href: "https://play.google.com/store/apps/details?id=com.dorukarslan.studyPacer" },
    ],
    screenshots: shots("learnFlow", 4, 546, 1186),
  },
  {
    id: "daly-bms-monitoring",
    name: "Daly BMS Monitoring",
    description: [
      "This application is specifically designed for monitoring Daly BMS batteries by capturing critical metrics such as voltage, temperature, and current via an MCP2515 module interfaced with CAN High and Low lines. An Arduino handles real-time data parsing to ensure precise and reliable readings.",
      "For wireless communication, the system leverages the HM10 Bluetooth module, enabling low-energy and efficient data transmission. The user interface, built with Flutter, utilizes the Flutter Blue Plus library to scan for nearby Bluetooth devices and display live battery data in a clean, user-friendly format. The app delivers comprehensive insights into battery health and performance, making it a powerful tool for diagnostics and real-time monitoring.",
    ],
    stack: [
      { label: "Frontend", value: "Flutter, GetX, Firebase Cloud Messaging" },
      {
        label: "Backend",
        value: "FastAPI, Docker, MongoDB, WebSockets (Custom Real-Time Chat), Redis, Firebase Storage",
      },
      { label: "Cloud & CI/CD", value: "AWS, GitHub Actions, GitHub" },
    ],
    links: [
      { type: "playstore", href: "https://play.google.com/store/apps/details?id=com.egearslan.dalybmsmonitoring" },
    ],
    screenshots: shots("DalyBmsMonitoring", 4, 569, 1186),
  },
];
